import { url } from "@/utils";
import React, { createContext, useContext, useState } from "react";

interface MetaData {
  title: string;
  description: string;
  keywords: string[];
  url?: string;
  image?: string;
  robots?: string;
  breadcrumbs?: { name: string; url: string }[];
  organizationSchema?: {};
}

interface MetaContextProps {
  meta: MetaData;
  setMeta: (newMeta: Partial<MetaData>) => void;
}

const MetaContext = createContext<MetaContextProps | undefined>(undefined);

export const MetaProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "https://saycoonline.com";

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "SAY MEDYA TİCARET LTD. ŞTİ.",
    url: "https://saycoonline.com",
    logo: "https://saycoonline.com/logo.png",
    description:
      "SAY MEDYA TİCARET LTD. ŞTİ., atölyeler ve online kurslar sunan bir eğitim platformudur.",
    foundingDate: "2020-01-01",
    founders: [{ "@type": "Person", name: "Burçin Alpacar" }],
    address: {
      "@type": "PostalAddress",
      streetAddress:
        "Fethiye MH. Sanayi cd. Corner plus iş merkezi no:263/A REGUS OFİSLER Nilüfer",
      addressLocality: "Bursa",
      addressCountry: "TR",
      postalCode: "16140",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+90 (224) 234 02 37",
      contactType: "customer service",
      email: "info@saycoonline.com",
    },
    sameAs: [
      "https://www.facebook.com/saycoonline",
      "https://www.instagram.com/saycoonline",
      "https://www.linkedin.com/company/saycoonline",
    ],
  };

  const defaultMeta: MetaData = {
    title: "Say&co Online",
    description: "Binlerce Atölye sizlerle",
    keywords: ["default", "seo", "react"],
    url: window.location.href,
    image: "/default-image.png",
    robots: "index, follow",
    breadcrumbs: [
      { name: "Ana Sayfa", url: baseUrl },
      { name: "Hakkımızda", url: baseUrl + url("Home.About") },
      {
        name: "Online Atölyeler",
        url: baseUrl + url("Home.About.Online-Workshop"),
      },
      {
        name: "Kurgusal Etkinlik",
        url: baseUrl + url("Home.About.Fictional-Workshop"),
      },
      { name: "Atölyeler", url: baseUrl + url("Home.Workshops") },
      { name: "Kurgusal Etkinlik", url: baseUrl + url("Home.Online") },
      { name: "İletişim", url: baseUrl + url("Home.Contact") },
    ],
    organizationSchema: organizationSchema,
  };

  const [meta, setMetaState] = useState<MetaData>(defaultMeta);

  // ✅ Önceki değerle aynıysa güncelleme yapma (Sonsuz Döngü Engellendi)
  const updateMeta = (newMeta: Partial<MetaData>) => {
    setMetaState((prevMeta) => {
      const mergedMeta = { ...prevMeta, ...newMeta };

      // Eğer yeni meta önceki ile tamamen aynıysa state güncelleme
      if (JSON.stringify(prevMeta) === JSON.stringify(mergedMeta)) {
        return prevMeta;
      }

      return mergedMeta;
    });
  };

  return (
    <MetaContext.Provider value={{ meta, setMeta: updateMeta }}>
      {children}
    </MetaContext.Provider>
  );
};

export const useMeta = () => {
  const context = useContext(MetaContext);
  if (!context) {
    throw new Error("useMeta must be used within a MetaProvider");
  }
  return context;
};
